import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Phone, Loader2, MessageSquare, AlertTriangle, Target,
  Copy, Check, Lightbulb, ClipboardList, FileText, TrendingUp, Gauge
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { API } from "../App";
import { toast } from "sonner";

const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

export default function PreCallCopilot({ leadId, leadName, open, onClose }) {
  const [scriptData, setScriptData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && !scriptData && !loading) fetchPrep();
  }, [open]);

  const fetchPrep = async () => {
    if (scriptData) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch both in parallel
      const [scriptRes, summaryRes] = await Promise.allSettled([
        axios.get(`${API}/ai-screens/pre-call/${leadId}`),
        axios.get(`${API}/ai/lead-summary/${leadId}`),
      ]);
      if (scriptRes.status === "fulfilled") setScriptData(scriptRes.value.data);
      if (summaryRes.status === "fulfilled") setSummaryData(summaryRes.value.data);
      if (scriptRes.status === "rejected" && summaryRes.status === "rejected") {
        throw new Error("Failed to generate prep");
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to generate prep");
      toast.error("Failed to generate prep");
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (isOpen) => {
    if (!isOpen) onClose();
  };

  const data = scriptData;

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pre-call-copilot-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Manrope']">
            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Phone className="w-4 h-4 text-indigo-600" />
            </div>
            Prep Me — {leadName}
          </DialogTitle>
          <p className="text-sm text-slate-500">AI-generated summary + call script for your next interaction</p>
        </DialogHeader>

        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            <p className="text-sm text-slate-500">Generating your prep brief...</p>
          </div>
        )}

        {error && !data && !summaryData && (
          <div className="flex items-center gap-2 p-4 bg-red-50 rounded-lg text-red-700 text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {(data || summaryData) && !loading && (
          <div className="space-y-4 mt-2">

            {/* ── SUMMARY SECTION ── */}
            {summaryData && (
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Lead Summary</p>
                  </div>
                  {summaryData.readiness_score != null && (
                    <div className="flex items-center gap-1.5">
                      <Gauge className="w-3.5 h-3.5 text-blue-600" />
                      <span className="text-lg font-bold text-blue-600">{summaryData.readiness_score}%</span>
                      <span className="text-[10px] text-blue-500">readiness</span>
                    </div>
                  )}
                </div>

                {summaryData.profile_summary && (
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <p className="text-sm text-blue-900">{summaryData.profile_summary}</p>
                    <CopyBtn text={summaryData.profile_summary} />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {summaryData.key_requirements?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-blue-600 mb-1">KEY REQUIREMENTS</p>
                      <ul className="space-y-0.5">
                        {summaryData.key_requirements.map((r, i) => (
                          <li key={i} className="text-xs text-blue-800 flex items-start gap-1"><span className="text-blue-400">+</span> {r}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {summaryData.likely_objections?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-amber-600 mb-1">LIKELY OBJECTIONS</p>
                      <ul className="space-y-0.5">
                        {summaryData.likely_objections.map((o, i) => (
                          <li key={i} className="text-xs text-amber-800 flex items-start gap-1"><span className="text-amber-400">!</span> {o}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {summaryData.sentiment_trajectory && (
                  <div className="mt-2 flex items-center gap-2">
                    <TrendingUp className="w-3 h-3 text-blue-500" />
                    <span className="text-[10px] text-blue-600 capitalize">Sentiment: {summaryData.sentiment_trajectory}</span>
                  </div>
                )}
              </div>
            )}

            {/* ── SCRIPT SECTION ── */}
            {data && (
              <>
                {/* Lead Quick View */}
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="outline" className="capitalize">{data.lead?.stage?.replace(/_/g, " ")}</Badge>
                  <Badge variant="outline" className="capitalize">{data.lead?.score}</Badge>
                  {data.lead?.project_interest && <Badge variant="outline">{data.lead.project_interest}</Badge>}
                  {data.lead?.budget_max && <Badge variant="outline">Budget: up to {(data.lead.budget_max / 100000).toFixed(0)}L</Badge>}
                </div>

                {/* Opening */}
                <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Opening</p>
                    <CopyBtn text={data.script?.opening || ""} />
                  </div>
                  <p className="text-sm text-indigo-900">{data.script?.opening}</p>
                </div>

                {/* Key Talking Points */}
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Target className="w-3.5 h-3.5 text-emerald-600" />
                    <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Key Points</p>
                  </div>
                  <ul className="space-y-1.5">
                    {data.script?.key_points?.map((pt, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{i + 1}</span>
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Qualifying Questions */}
                {data.script?.qualifying_questions?.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Lightbulb className="w-3.5 h-3.5 text-amber-600" />
                      <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Ask These Questions</p>
                    </div>
                    <ul className="space-y-1.5">
                      {data.script.qualifying_questions.map((q, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                          <span className="text-amber-500 mt-0.5">?</span> {q}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Objection Responses */}
                {data.script?.objection_responses?.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <MessageSquare className="w-3.5 h-3.5 text-red-500" />
                      <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">If They Object...</p>
                    </div>
                    <div className="space-y-2">
                      {data.script.objection_responses.map((obj, i) => (
                        <div key={i} className="text-sm">
                          <p className="text-red-600 font-medium">"{obj.objection}"</p>
                          <p className="text-slate-700 mt-0.5 pl-3 border-l-2 border-emerald-300">{obj.response}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Closing */}
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">Closing</p>
                    <CopyBtn text={data.script?.closing || ""} />
                  </div>
                  <p className="text-sm text-emerald-900">{data.script?.closing}</p>
                </div>

                {/* Tone Guidance */}
                {data.script?.tone_guidance && (
                  <p className="text-xs text-slate-500 italic text-center px-4">{data.script.tone_guidance}</p>
                )}

                {/* Pending Tasks Alert */}
                {data.pending_tasks?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <ClipboardList className="w-3.5 h-3.5 text-amber-600" />
                      <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Pending Tasks</p>
                    </div>
                    {data.pending_tasks.map((t, i) => (
                      <p key={i} className="text-sm text-amber-800">{t.title} — due {t.due ? new Date(t.due).toLocaleDateString() : "N/A"}</p>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
